// src/services/api.js
import apiClient from "./axiosInstance";

// ==================== AUTH API ====================
export const authAPI = {
  // ==================== DEVICE TOKEN ====================

  // Device Token Register (FCM)
  registerDeviceToken: (data) => {
    return apiClient.post("/DeviceToken/", data);
  },

  // Logout with device token cleanup
  logout: (data) => {
    return apiClient.post("/Logout/", data);
  },

  // ==================== SIGNUP FLOW ====================

  // Sign up - Send OTP to email
  signup: (data) => {
    return apiClient.post("/SendOtp/", data);
  },

  // Verify OTP after signup
  verifyOtp: (data) => {
    return apiClient.post("/VerifyOtp/", data);
  },

  // Resend OTP
  resendOtp: (data) => {
    return apiClient.post("/ResendOtp/", data);
  },

  // Set password after OTP verification
  setPassword: (data) => {
    return apiClient.post("/SetPassword/", data);
  },

  // ==================== LOGIN FLOW ====================

  // Login with email and password
  login: (data) => {
    return apiClient.post("/Login/", data);
  },

  // ==================== FORGOT PASSWORD FLOW ====================

  // Send OTP for forgot password
  sendForgotPasswordOtp: (data) => {
    return apiClient.post("/ProviderSendForgotPasswordOTP/", data);
  },

  // Verify OTP for forgot password
  verifyForgotPasswordOtp: (data) => {
    return apiClient.post("/ProviderVerifyForgotPasswordOTP/", data);
  },

  // Reset password
  resetPassword: (data) => {
    return apiClient.post("/ProviderResetPasswordView/", data);
  },

  // Google login
  googleLogin: (data) => {
    return apiClient.post("/GoogleLogin/", data);
  },

  // Apple login
  appleLogin: (data) => {
    return apiClient.post("/AppleLogin/", data);
  },

  // ==================== BUSINESS PROFILE SETUP ====================

  // Submit Basic Info (Step 1)
  submitBasicInfo: (data) => {
    return apiClient.post("/BasicInfo/", data);
  },

  // Upload Document (Step 2) - Single document upload
  uploadDocument: (data) => {
    return apiClient.post("/UploadDocument/", data);
  },

  // Upload Multiple Documents
  uploadMultipleDocuments: (data) => {
    return apiClient.post("/UploadDocumentsMultiple/", data);
  },

  // Get Document Types
  getDocumentTypes: () => {
    return apiClient.get("/DocumentTypes/");
  },
};

// ==================== NOTIFICATION API ====================
export const notificationAPI = {
  // Get notifications list with optional category filter
  // category: ALL, UPDATES, SERVICES, DRIVERS, OTHERS
  getNotifications: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category && params.category !== "ALL") {
      queryParams.append("category", params.category);
    }
    if (params.page) queryParams.append("page", params.page);
    const queryString = queryParams.toString();
    return apiClient.get(
      `/NotificationsList/${queryString ? "?" + queryString : ""}`
    );
  },

  // Mark single notification as read
  markAsRead: (data) => {
    return apiClient.post("/NotificationsList/MarkRead/", data);
  },

  // Mark all notifications as read (optionally by category)
  markAllAsRead: (data = {}) => {
    return apiClient.post("/NotificationsList/MarkRead/", {
      mark_all: true,
      ...data,
    });
  },
};

// ==================== PROFILE API ====================
export const profileAPI = {
  // Get Profile (includes both User and BusinessDetails)
  getProfile: () => {
    return apiClient.get("/Profile/");
  },

  // Get Provider Reviews
  getProviderReviews: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.page) queryParams.append("page", params.page);
    const queryString = queryParams.toString();
    return apiClient.get(
      `/ProviderReviews/${queryString ? "?" + queryString : ""}`
    );
  },

  // Get Business Details
  getBusinessDetails: () => {
    return apiClient.get("/BusinessDetails/");
  },

  // Update Business Details
  updateBusinessDetails: (data) => {
    return apiClient.post("/BusinessDetails/", data);
  },

  // Get Documents List
  getDocuments: () => {
    return apiClient.get("/ProviderDocuments/");
  },

  // Get Document Types (for dropdown/selection)
  getDocumentTypes: () => {
    return apiClient.get("/DocumentTypes/");
  },

  // Upload Single Document
  uploadDocument: (data) => {
    return apiClient.post("/UploadDocument/", data);
  },

  // Upload Multiple Documents
  uploadMultipleDocuments: (data) => {
    return apiClient.post("/UploadDocumentsMultiple/", data);
  },

  // Get Bank Details
  getBankDetails: () => {
    return apiClient.get("/BankDetailsView/");
  },

  // Change Password
  changePassword: (data) => {
    return apiClient.post("/ChangePassword/", data);
  },
};

// ==================== DRIVER API ====================
export const driverAPI = {
  // Invite Driver
  inviteDriver: (data) => {
    return apiClient.post("/InviteDriver/", data);
  },

  // Get all drivers list
  getDriversList: (page = 1) => {
    return apiClient.get(`/Drivers/?page=${page}`);
  },

  // Get single driver by ID (using query parameter)
  getDriverById: (id) => {
    return apiClient.get(`/Drivers/?DriverId=${id}`);
  },

  // Update driver details (Personal info like name, email, phone)
  updateDriver: (data) => {
    return apiClient.put("/Drivers/", data);
  },

  // Verify Driver (Approve/Reject/Suspend/Enable)
  verifyDriver: (data) => {
    return apiClient.post("/VerifyDriver/", data);
  },

  // Get Driver Vehicle Details
  getDriverVehicleDetails: (driverId) => {
    return apiClient.get(`/DriverVehicleDetails/?DriverId=${driverId}`);
  },

  // Update Driver Vehicle Details
  updateDriverVehicleDetails: (data) => {
    return apiClient.put(
      `/DriverVehicleDetails/?DriverId=${data.DriverId}`,
      data
    );
  },

  // Get Driver Documents
  getDriverDocuments: (driverId) => {
    return apiClient.get(`/DriverDocuments/?DriverId=${driverId}`);
  },

  // Upload Driver Documents (FormData with multiple docs)
  uploadDriverDocuments: (formData) => {
    return apiClient.post("/DriverDocuments/", formData);
  },

  // Get Driver Tasks
  getDriverTasks: (driverId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    const queryString = queryParams.toString();
    return apiClient.get(
      `/DriverTasks/${driverId}/${queryString ? "?" + queryString : ""}`
    );
  },

  // Get Driver Reviews
  getDriverReviews: (driverId) => {
    return apiClient.get(`/DriverReviews/${driverId}/`);
  },
};

// ==================== SERVICES API ====================
export const servicesAPI = {
  // Get all services with sub-services
  getServices: () => {
    return apiClient.get("/Services/");
  },

  // Enable/Disable services or sub-services
  toggleServices: (data) => {
    return apiClient.post("/Services/", data);
  },
};

// ==================== BOOKING API ====================
export const bookingAPI = {
  // Get booking counts for sidebar badge
  getBookingCounts: () => {
    return apiClient.get("/BookingCounts/");
  },

  // Get all bookings with filters
  // status: New, Ongoing, Completed, Cancelled
  // sort_by: newest, oldest, highest_amount, lowest_amount
  getBookings: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append("status", params.status);
    if (params.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params.page) queryParams.append("page", params.page);
    const queryString = queryParams.toString();
    return apiClient.get(`/Bookings/${queryString ? "?" + queryString : ""}`);
  },

  // Get single booking details
  getBookingDetails: (bookingId) => {
    return apiClient.get(`/Bookings/${bookingId}/`);
  },

  // Get available drivers for a booking
  getAvailableDrivers: (bookingId, search = "") => {
    const url = search
      ? `/AvailableDrivers/${bookingId}/?search=${encodeURIComponent(search)}`
      : `/AvailableDrivers/${bookingId}/`;
    return apiClient.get(url);
  },

  // Assign driver to booking
  assignDriver: (data) => {
    return apiClient.post("/AssignDriver/", data);
  },

  // Change/Edit driver for booking
  editBookingDriver: (data) => {
    return apiClient.post("/EditBookingDriver/", data);
  },

  // Accept or Reject booking
  acceptBooking: (data) => {
    return apiClient.post("/AcceptBooking/", data);
  },

  // Update booking status (Mark Arrived, Start Service, Mark Complete)
  updateBookingStatus: (data) => {
    return apiClient.post("/UpdateBookingStatus/", data);
  },
};

// ==================== VEHICLE TYPES API ====================
export const vehicleTypesAPI = {
  // Get all vehicle types
  getVehicleTypes: () => {
    return apiClient.get("/VehicleTypes/");
  },
};

// ==================== AVAILABILITY API ====================
export const availabilityAPI = {
  // Get paused and scheduled services
  getPausedServices: () => {
    return apiClient.get("/PauseAvailability/");
  },

  // Create new pause (immediate or scheduled)
  createPause: (data) => {
    return apiClient.post("/PauseAvailability/", data);
  },

  // Resume a paused service
  resumeService: (data) => {
    return apiClient.post("/PauseAvailability/Resume/", data);
  },

  // Cancel a scheduled pause
  cancelScheduledPause: (data) => {
    return apiClient.post("/PauseAvailability/Cancel/", data);
  },
};

// ==================== SERVICE RADIUS API ====================
export const serviceRadiusAPI = {
  // Get all services with radius settings
  getServicesRadius: () => {
    return apiClient.get("/ServicesRadius/");
  },

  // Update service radius
  updateServiceRadius: (data) => {
    return apiClient.post("/ServicesRadius/", data);
  },
};

// ==================== SUPPORT/FAQ API ====================
export const supportAPI = {
  // Get FAQs with optional category filter (uses Provider base)
  getFAQs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append("Category", params.category);
    const queryString = queryParams.toString();
    return apiClient.get(
      `/ProviderFAQ/${queryString ? "?" + queryString : ""}`
    );
  },

  // Get support tickets list with filters
  getTickets: (params = {}) => {
    const queryParams = new URLSearchParams();
    queryParams.append("app_type", "PROVIDER");
    if (params.status) queryParams.append("status", params.status);
    if (params.category) queryParams.append("category", params.category);
    if (params.page) queryParams.append("page", params.page);
    return apiClient.get(`/Support/api/v1/Support/?${queryParams.toString()}`, {
      baseURL: "",
    });
  },

  // Get support categories for dropdown
  getCategories: () => {
    return apiClient.get(
      "/Support/api/v1/Support/Categories/?app_type=PROVIDER",
      {
        baseURL: "",
      }
    );
  },

  // Create new support ticket
  createTicket: (data) => {
    return apiClient.post(
      "/Support/api/v1/Support/Create/",
      {
        Subject: data.subject,
        Description: data.description,
        CategoryKey: data.categoryKey,
        Priority: 2,
        AppType: "PROVIDER",
      },
      {
        baseURL: "",
      }
    );
  },

  // Get single ticket details with replies
  getTicketDetails: (ticketId, page = 1) => {
    return apiClient.get(`/Support/api/v1/Support/${ticketId}/?page=${page}`, {
      baseURL: "",
    });
  },

  // Send reply to ticket
  sendReply: (ticketId, body) => {
    return apiClient.post(
      `/Support/api/v1/Support/${ticketId}/Reply/`,
      {
        Body: body,
      },
      {
        baseURL: "",
      }
    );
  },
};

// ==================== EARNINGS API ====================
export const earningsAPI = {
  // Get earnings summary and service orders
  getEarnings: () => {
    return apiClient.get("/ProviderEarningsView/");
  },

  // Get transactions with pagination
  getTransactions: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page);
    if (params.page_size) queryParams.append("page_size", params.page_size);
    const queryString = queryParams.toString();
    return apiClient.get(
      `/ProviderTransactionsView/${queryString ? "?" + queryString : ""}`
    );
  },

  // Get bookings count for chart data
  getBookingsCount: () => {
    return apiClient.get("/ProviderBookingsCount/");
  },
};

// ==================== DASHBOARD API ====================
export const dashboardAPI = {
  // Get dashboard summary (stats cards)
  getSummary: (period = "month") => {
    return apiClient.get(`/Dashboard/Summary/?period=${period}`);
  },

  // Get sales overview chart data
  getSalesOverview: (period = "month") => {
    return apiClient.get(`/Dashboard/SalesOverview/?period=${period}`);
  },

  // Get most used services
  getMostUsedServices: (limit = 5) => {
    return apiClient.get(`/Dashboard/MostUsedServices/?limit=${limit}`);
  },

  // Get top customers
  getTopCustomers: (limit = 5, sortBy = "orders") => {
    return apiClient.get(
      `/Dashboard/TopCustomers/?limit=${limit}&sort_by=${sortBy}`
    );
  },

  // Get recent bookings
  getRecentBookings: (page = 1, pageSize = 5) => {
    return apiClient.get(
      `/Dashboard/RecentBookings/?page=${page}&page_size=${pageSize}`
    );
  },

  // Get driver performance
  getDriverPerformance: (limit = 5, sortBy = "deliveries") => {
    return apiClient.get(
      `/Dashboard/DriverPerformance/?limit=${limit}&sort_by=${sortBy}`
    );
  },

  // Get latest reviews
  getLatestReviews: (page = 1, pageSize = 5) => {
    return apiClient.get(
      `/Dashboard/LatestReviews/?page=${page}&page_size=${pageSize}`
    );
  },
};

// ==================== PAYOUTS API ====================
export const payoutsAPI = {
  // Get payouts summary
  getSummary: () => {
    return apiClient.get("/Payouts/Summary/");
  },

  // Get payouts list with optional filters
  getPayouts: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.from_date) queryParams.append("from_date", params.from_date);
    if (params.to_date) queryParams.append("to_date", params.to_date);
    if (params.status) queryParams.append("status", params.status);
    if (params.page) queryParams.append("page", params.page);
    const queryString = queryParams.toString();
    return apiClient.get(`/Payouts/${queryString ? "?" + queryString : ""}`);
  },

  // Get single payout details with items (bookings)
  getPayoutDetails: (payoutId) => {
    return apiClient.get(`/Payouts/${payoutId}/`);
  },

  // Download invoice for a payout
  downloadInvoice: (payoutId) => {
    return apiClient.get(`/Payouts/${payoutId}/Invoice/`, {
      responseType: "blob",
    });
  },
};

// ==================== LEGAL API ====================
export const legalAPI = {
  // Get Terms and Conditions
  getTermsAndConditions: () => {
    return apiClient.get(
      "/Legal/api/v1/Legal/TERMS_AND_CONDITIONS/?app=PROVIDER",
      {
        baseURL: "",
      }
    );
  },

  // Get Privacy Policy
  getPrivacyPolicy: () => {
    return apiClient.get("/Legal/api/v1/Legal/PRIVACY_POLICY/?app=PROVIDER", {
      baseURL: "",
    });
  },
};

// ==================== FLASH SALES API ====================
export const flashSalesAPI = {
  getFlashSales: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.tab) queryParams.append('Tab', params.tab);
    if (params.search) queryParams.append('Search', params.search);
    if (params.page) queryParams.append('Page', params.page);
    const queryString = queryParams.toString();
    return apiClient.get(`/FlashSales/${queryString ? '?' + queryString : ''}`);
  },
  createFlashSale: (data) => apiClient.post('/FlashSales/', data),
  updateFlashSale: (flashId, data) => apiClient.put(`/FlashSales/${flashId}/`, data),
  deleteFlashSale: (flashId) => apiClient.delete(`/FlashSales/${flashId}/`),
};

// ==================== ENABLED SERVICES API ====================
export const enabledServicesAPI = {
  getEnabledServices: () => {
    return apiClient.get('/EnabledServices/');
  },
};

// ==================== BASE LOCATIONS API ====================
export const serviceLocationsAPI = {
  getBaseLocations: () => apiClient.get('/BaseLocations/'),
  createBaseLocation: (data) => apiClient.post('/BaseLocations/', data),
  updateBaseLocation: (baseId, data) => apiClient.put(`/BaseLocations/${baseId}/`, data),
  deleteBaseLocation: (baseId) => apiClient.delete(`/BaseLocations/${baseId}/`),
  getBaseLocationDetail: (baseId) => apiClient.get(`/BaseLocations/${baseId}/`),
  toggleBaseService: (baseId, serviceId, data) =>  apiClient.patch(`/BaseLocations/${baseId}/Service/${serviceId}/Toggle/`, data),
  getServiceVariants:      (baseId, serviceId)       => apiClient.get(`/BaseLocations/${baseId}/Service/${serviceId}/Variants/`),
updateServiceVariants:   (baseId, serviceId, data) => apiClient.put(`/BaseLocations/${baseId}/Service/${serviceId}/Variants/`, data),
getServiceAvailability:  (baseId, serviceId)       => apiClient.get(`/BaseLocations/${baseId}/Service/${serviceId}/Availability/`),
saveServiceAvailability: (baseId, serviceId, data) => apiClient.post(`/BaseLocations/${baseId}/Service/${serviceId}/Availability/`, data),
addServiceAvailabilitySlot: (baseId, serviceId, data) =>
  apiClient.post(`/BaseLocations/${baseId}/Service/${serviceId}/Availability/AddSlot/`, data),
getBaseLocationsList: () => apiClient.get('/BaseLocations/List/'),
};

export const serviceProfileAPI = {
  getProfile:    (serviceId)              => apiClient.get(`/ServiceProfile/${serviceId}/`),
  updateProfile: (serviceId, data)        => apiClient.put(`/ServiceProfile/${serviceId}/`, data),
  patchProfile:  (serviceId, data)        => apiClient.patch(`/ServiceProfile/${serviceId}/`, data),
  getImages:     (serviceId)              => apiClient.get(`/ServiceProfile/${serviceId}/Images/`),
  uploadImage:   (serviceId, formData)    => apiClient.post(`/ServiceProfile/${serviceId}/Images/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteImage:   (serviceId, imageId)     => apiClient.delete(`/ServiceProfile/${serviceId}/Images/${imageId}/`),
};

export const holidayUpliftAPI = {
  getHolidayUplift:  (serviceId)       => apiClient.get(`/HolidayUplift/${serviceId}/`),
  saveHolidayUplift: (serviceId, data) => apiClient.post(`/HolidayUplift/${serviceId}/`, data),
};

// ==================== ALL SERVICES API (catalogue dropdown) ====================
export const allServicesAPI = {
  getAllServices: () => apiClient.get('/AllServices/'),
};

export default apiClient;
